/**
 * Utility function to resolve a theme value by its dot-notated path.
 * It navigates the preset object, resolves any reference tokens (e.g., '{primitive.red.600}'),
 * and returns the final primitive value (e.g., a hex color).
 *
 * @param preset The theme preset object (e.g., CsxInternalToolsDarkPreset).
 * @param path The dot-notated path to the value (e.g., 'semantic.colorScheme.light.severities.errorMessage.color').
 * @returns The resolved primitive value as a string.
 * @throws Error if the path is invalid, a reference loop is detected, or the value cannot be resolved.
 */
export function resolvePresetValue(preset: any, path: string): string {
  const visited = new Set<string>(); // For cycle detection

  let currentPath = path;
  while (true) {
    if (visited.has(currentPath)) {
      throw new Error(`Cycle detected in theme references at path: ${currentPath}`);
    }
    visited.add(currentPath);

    let value = getValueByPath(preset, currentPath);
    if (!isReference(value)) {
      return value;
    }
    currentPath = extractReferencePath(value);
  }
}

/**
 * Helper function to get a value from an object using a dot-notated path.
 * Supports string keys like '600' for color scales.
 *
 * @param obj The object to navigate.
 * @param path The dot-notated path (e.g., 'primitive.red.600').
 * @returns The value at the path.
 * @throws Error if the path is invalid.
 */
function getValueByPath(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === undefined || current === null) {
      throw new Error(`Invalid path in theme preset: ${path}`);
    }
    current = current[part];
  }
  if (current === undefined) {
    throw new Error(`Invalid path in theme preset: ${path}`);
  }
  return current;
}

/**
 * Checks if a value is a theme reference token (e.g., '{some.path}').
 *
 * @param value The value to check.
 * @returns True if it's a reference token.
 */
function isReference(value: any): boolean {
  return typeof value === 'string' && value.startsWith('{') && value.endsWith('}');
}

/**
 * Extracts the path from a reference token (e.g., '{primitive.red.600}' -> 'primitive.red.600').
 *
 * @param value The reference token.
 * @returns The extracted path.
 * @throws Error if the value is not a valid reference.
 */
function extractReferencePath(value: string): string {
  if (!isReference(value)) {
    throw new Error(`Value is not a reference: ${value}`);
  }
  return value.slice(1, -1);
}

// Example usage:
// const errorColor = resolvePresetValue(CsxInternalToolsDarkPreset, 'semantic.colorScheme.light.severities.errorMessage.color');
// console.log(errorColor); // Outputs the hex value, e.g., '#c53030'
